import { BlobProvider } from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import authHeader from "services/auth-header";
import API from "services/axios";
import { ShipmentPdf } from "./ShipmentPdf";
import Spinner from "components/Spinner";

export default function ShipmentPdfButton({
  btnClasses,
  btnColor,
  shipment,
  products,
}) {
  /*   All States
   ********************************************* */
  const [showPdf, setShowPdf] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [locationLogo, setLocationLogo] = useState(null);
  const [locationInfo, setLocationInfo] = useState(null);
  const [productsListWithImages, setProductsListWithImages] = useState(null);
  const [enrichedShipment, setEnrichedShipment] = useState(null);
  const [loading, setLoading] = useState(false);

  /*   All Functions
   ********************************************* */
  const getShippingMethodsAndCouriers = async () => {
    try {
      // Fetch shipping methods and courier services
      const [shippingMethodsRes, courierServicesRes] = await Promise.all([
        API.get("/shipmentmethods", { headers: authHeader() }),
        API.get("/courier-services", { headers: authHeader() }),
      ]);

      const shippingMethods = shippingMethodsRes.data || [];
      const courierServices = courierServicesRes.data || [];

      // Find the matching shipping method and courier service
      const shippingMethod = shippingMethods.find(
        (method) => method.id === shipment?.shippingMethodId
      );
      const courierService = courierServices.find(
        (service) => service.id === shipment?.courierServiceId
      );

      // Create enriched shipment object with related data
      const enriched = {
        ...shipment,
        shippingMethod: shippingMethod || null,
        courierService: courierService || null,
      };

      setEnrichedShipment(enriched);
      return enriched;
    } catch (error) {
      console.error(
        "Error fetching shipping methods or courier services:",
        error
      );
      // Return shipment as-is if fetch fails
      setEnrichedShipment(shipment);
      return shipment;
    }
  };
  const getLocationLogo = async () => {
    try {
      // Get customer's locationId
      const locationId = shipment?.customer?.locationId;
      if (!locationId) {
        console.log("No locationId found for customer");
        return null;
      }

      // Fetch all locations
      const { data: locationsData } = await API.get("/locations", {
        headers: authHeader(),
      });

      // Find the location that matches the customer's locationId
      const location = locationsData?.find((loc) => loc.id === locationId);

      if (!location || !location.pdfLogo) {
        console.log("No location or pdfLogo found for locationId:", locationId);
        return null;
      }

      // Fetch the actual image using the pdfLogo path
      const { data: imageData } = await API.post(
        "/design-request/get-image",
        { path: location.pdfLogo },
        {
          headers: authHeader(),
        }
      );

      setLocationLogo(imageData);
      return { logo: imageData, location };
    } catch (error) {
      console.error("Error fetching location logo:", error);
      return null;
    }
  };

  const handlePdfDownload = (blob) => {
    // Create a URL for the blob
    const blobUrl = URL.createObjectURL(blob);

    // Create a temporary anchor element
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `Shipment-${shipment?.shipmentId}.pdf`;

    // Append to the body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the blob URL to free up memory
    URL.revokeObjectURL(blobUrl);
  };

  const getAllProductsImages = async (productList) => {
    try {
      setLoading(true);
      const updatedProducts = await Promise.all(
        productList?.map(async (item) => {
          // Flatten product data - merge WOProduct fields to root level
          const flattenedItem = {
            ...item,
            // If WOProduct exists, merge its fields at root level
            productType: item?.productType || item?.WOProduct?.productType,
            productTypes: item?.productTypes || item?.WOProduct?.productTypes,
            productStyle: item?.productStyle || item?.WOProduct?.productStyle,
            productDescription:
              item?.productDescription || item?.WOProduct?.productDescription,
            productColor: item?.productColor || item?.WOProduct?.productColor,
            productFabric:
              item?.productFabric || item?.WOProduct?.productFabric,
            notes: item?.notes || item?.WOProduct?.notes,
          };

          const productImage =
            item?.productImage || item?.WOProduct?.productImage;
          if (productImage) {
            try {
              const { data } = await API.post(
                "/design-request/get-image",
                { path: productImage },
                {
                  headers: authHeader(),
                }
              );
              return {
                ...flattenedItem,
                productImage: data,
                WOProduct: item?.WOProduct
                  ? {
                      ...item.WOProduct,
                      productImage: data,
                    }
                  : undefined,
              };
            } catch (error) {
              console.error("Error fetching product image:", error);
              return flattenedItem;
            }
          }
          return flattenedItem;
        })
      );
      setProductsListWithImages(updatedProducts);
      setLoading(false);
      return updatedProducts;
    } catch (error) {
      console.error("Error in getAllProductsImages:", error);
      setLoading(false);
      return productList;
    }
  };

  const handleGeneratePdf = async () => {
    setShowPdfModal(true);
    setLoading(true);

    try {
      // Fetch and enrich shipment with shipping method and courier data
      await getShippingMethodsAndCouriers();

      // Get location logo if customer has a locationId
      if (shipment?.customer?.locationId) {
        const locationData = await getLocationLogo();
        if (locationData?.location) {
          setLocationInfo(locationData.location);
        }
      }

      // Get all product images
      if (products && products.length > 0) {
        await getAllProductsImages(products);
      }

      setShowPdf(true);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    if (!showPdfModal) {
      setShowPdf(false);
      setProductsListWithImages(null);
      setLocationLogo(null);
      setEnrichedShipment(null);
    }
  }, [showPdfModal]);

  return (
    <>
      <button
        className={btnClasses || "btn btn-sm btn-primary text-nowrap"}
        style={{
          backgroundColor: btnColor || "",
        }}
        onClick={handleGeneratePdf}
        disabled={!products || products.length === 0}
      >
        Generate PDF
      </button>

      <Modal
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Shipment PDF - {shipment?.shipmentId}</Modal.Title>
        </Modal.Header>

        {loading && (
          <>
            <Modal.Body>
              <div className="text-center py-5">
                <Spinner />
                <p className="mt-3">Loading PDF...</p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                className="btn btn-secondary"
                onClick={() => setShowPdfModal(false)}
              >
                Close
              </button>
            </Modal.Footer>
          </>
        )}

        {!loading && showPdf && productsListWithImages && enrichedShipment && (
          <BlobProvider
            document={
              <ShipmentPdf
                shipment={enrichedShipment}
                productsList={productsListWithImages}
                locationLogo={locationLogo}
                locationInfo={locationInfo}
              />
            }
          >
            {({ blob, url, loading: pdfLoading, error }) => {
              if (pdfLoading) {
                return (
                  <>
                    <Modal.Body>
                      <div className="text-center py-5">
                        <Spinner />
                        <p className="mt-3">Generating PDF...</p>
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowPdfModal(false)}
                      >
                        Close
                      </button>
                    </Modal.Footer>
                  </>
                );
              }

              if (error) {
                return (
                  <>
                    <Modal.Body>
                      <div className="alert alert-danger">
                        Error generating PDF: {error.message}
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowPdfModal(false)}
                      >
                        Close
                      </button>
                    </Modal.Footer>
                  </>
                );
              }

              return (
                <>
                  <Modal.Body>
                    <iframe
                      src={url}
                      style={{ width: "100%", height: "70vh" }}
                      title="Shipment PDF Preview"
                    />
                  </Modal.Body>
                  <Modal.Footer>
                    <button
                      className="btn btn-primary"
                      onClick={() => handlePdfDownload(blob)}
                    >
                      Download PDF
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowPdfModal(false)}
                    >
                      Close
                    </button>
                  </Modal.Footer>
                </>
              );
            }}
          </BlobProvider>
        )}
      </Modal>
    </>
  );
}
